import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Drawer,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconBan,
  IconCheck,
  IconPlus,
  IconRotateClockwise,
  IconSearch,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/client';
import { employeesApi, hasPassportData, type Employee, type EmployeeInput } from '../api/employees';
import { organizationsApi } from '../api/organizations';
import { RegistryPage } from '../components/RegistryPage';
import { useAuth } from '../auth/AuthContext';

const emptyForm: EmployeeInput = {
  organizationId: '',
  lastName: '',
  firstName: '',
  middleName: '',
  position: '',
  personnelNumber: '',
  passportSeries: '',
  passportNumber: '',
  passportIssuedBy: '',
  passportIssueDate: '',
  passportCode: '',
  driverLicense: '',
  driverLicenseSeries: '',
  driverLicenseNumber: '',
  driverLicenseIssuedBy: '',
  driverLicenseIssueDate: '',
  isDriver: false,
  phone: '',
  inn: '',
};

function toDateInput(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

export function EmployeesPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [onlyDrivers, setOnlyDrivers] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EmployeeInput>(emptyForm);
  const [formError, setFormError] = useState<string[] | null>(null);

  const organizations = useQuery({ queryKey: ['organizations'], queryFn: () => organizationsApi.list() });

  const list = useQuery({
    queryKey: ['employees', { search: debouncedSearch, onlyDrivers, includeInactive }],
    queryFn: () => employeesApi.list({ search: debouncedSearch, onlyDrivers, includeInactive }),
  });

  const organizationOptions = (organizations.data?.items ?? []).map((o) => ({ value: o.id, label: o.shortName }));

  function openCreate() {
    setEditingId(null);
    // Одна организация — подставляем её сразу, выбирать не из чего
    const onlyOrganization = organizationOptions.length === 1 ? organizationOptions[0]!.value : '';
    setForm({ ...emptyForm, organizationId: onlyOrganization });
    setFormError(null);
    setDrawerOpen(true);
  }

  function openEdit(employee: Employee) {
    setEditingId(employee.id);
    setForm({
      organizationId: employee.organizationId,
      lastName: employee.lastName,
      firstName: employee.firstName,
      middleName: employee.middleName ?? '',
      position: employee.position ?? '',
      personnelNumber: employee.personnelNumber ?? '',
      passportSeries: employee.passportSeries ?? '',
      passportNumber: employee.passportNumber ?? '',
      passportIssuedBy: employee.passportIssuedBy ?? '',
      passportIssueDate: toDateInput(employee.passportIssueDate),
      passportCode: employee.passportCode ?? '',
      driverLicense: employee.driverLicense ?? '',
      driverLicenseSeries: employee.driverLicenseSeries ?? '',
      driverLicenseNumber: employee.driverLicenseNumber ?? '',
      driverLicenseIssuedBy: employee.driverLicenseIssuedBy ?? '',
      driverLicenseIssueDate: toDateInput(employee.driverLicenseIssueDate),
      isDriver: employee.isDriver,
      phone: employee.phone ?? '',
      inn: employee.inn ?? '',
    });
    setFormError(null);
    setDrawerOpen(true);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: EmployeeInput = {
        ...form,
        middleName: form.middleName || null,
        position: form.position || null,
        personnelNumber: form.personnelNumber || null,
        passportSeries: form.passportSeries || null,
        passportNumber: form.passportNumber || null,
        passportIssuedBy: form.passportIssuedBy || null,
        passportIssueDate: form.passportIssueDate || null,
        passportCode: form.passportCode || null,
        driverLicense: form.driverLicense || null,
        driverLicenseSeries: form.driverLicenseSeries || null,
        driverLicenseNumber: form.driverLicenseNumber || null,
        driverLicenseIssuedBy: form.driverLicenseIssuedBy || null,
        driverLicenseIssueDate: form.driverLicenseIssueDate || null,
        phone: form.phone || null,
        inn: form.inn || null,
      };
      return editingId ? employeesApi.update(editingId, payload) : employeesApi.create(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      notifications.show({ color: 'green', message: 'Сотрудник сохранён' });
      setDrawerOpen(false);
    },
  });

  function handleSubmit() {
    setFormError(null);
    const errors: string[] = [];
    if (!form.organizationId) errors.push('Укажите организацию');
    if (!form.lastName.trim()) errors.push('Укажите фамилию');
    if (!form.firstName.trim()) errors.push('Укажите имя');
    if (errors.length > 0) {
      setFormError(errors);
      return;
    }
    saveMutation.mutate(undefined, {
      onError: (err) => {
        setFormError(err instanceof ApiError ? (err.details ?? [err.message]) : ['Не удалось сохранить']);
      },
    });
  }

  const toggleActiveMutation = useMutation({
    mutationFn: (employee: Employee) =>
      employee.isActive ? employeesApi.deactivate(employee.id) : employeesApi.activate(employee.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['employees'] }),
  });

  const items = list.data?.items ?? [];

  return (
    <RegistryPage
      title="Сотрудники"
      action={
        canEdit && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Добавить сотрудника
          </Button>
        )
      }
      description={
        <Text size="sm" c="dimmed">
          На сотрудника выписывается доверенность М-2 на получение ТМЦ. Для этого нужны заполненные
          паспортные данные — без них доверенность не выписать.
        </Text>
      }
      filters={
        <Group>
          <TextInput
            placeholder="Поиск по ФИО, должности или табельному номеру"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={340}
          />
          <Checkbox
            label="Только водители"
            checked={onlyDrivers}
            onChange={(e) => setOnlyDrivers(e.currentTarget.checked)}
          />
          <Checkbox
            label="Показывать уволенных"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.currentTarget.checked)}
          />
        </Group>
      }
    >
      <Table striped highlightOnHover verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>ФИО</Table.Th>
            <Table.Th>Должность</Table.Th>
            <Table.Th>Таб. №</Table.Th>
            <Table.Th>Организация</Table.Th>
            <Table.Th>Паспорт</Table.Th>
            <Table.Th>Статус</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((employee) => (
            <Table.Tr key={employee.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(employee)}>
              <Table.Td>
                <Group gap={6}>
                  <Text size="sm" fw={500}>
                    {employee.fullName}
                  </Text>
                  {employee.isDriver && (
                    <Badge size="sm" variant="light" color="blue">
                      водитель
                    </Badge>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>{employee.position ?? '—'}</Table.Td>
              <Table.Td>{employee.personnelNumber ?? '—'}</Table.Td>
              <Table.Td>{employee.organization.shortName}</Table.Td>
              <Table.Td>
                {hasPassportData(employee) ? (
                  <Text size="sm">
                    {employee.passportSeries} {employee.passportNumber}
                  </Text>
                ) : (
                  <Badge size="sm" color="orange" variant="light">
                    не заполнен
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>
                <Badge color={employee.isActive ? 'green' : 'gray'} variant="light">
                  {employee.isActive ? 'Работает' : 'Уволен'}
                </Badge>
              </Table.Td>
              <Table.Td onClick={(e) => e.stopPropagation()}>
                {canEdit && (
                  <Tooltip label={employee.isActive ? 'Отметить уволенным' : 'Вернуть в работу'}>
                    <ActionIcon
                      variant="subtle"
                      color={employee.isActive ? 'red' : 'green'}
                      onClick={() => toggleActiveMutation.mutate(employee)}
                    >
                      {employee.isActive ? <IconBan size={16} /> : <IconRotateClockwise size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {!list.isLoading && items.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Сотрудники не найдены
        </Text>
      )}

      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingId ? 'Карточка сотрудника' : 'Новый сотрудник'}
        position="right"
        size="lg"
        padding="lg"
      >
        <Stack gap="sm">
          {formError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              <Stack gap={2}>
                {formError.map((e, i) => (
                  <Text key={i} size="sm">
                    {e}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}

          <Select
            label="Организация"
            placeholder="Выберите организацию"
            data={organizationOptions}
            value={form.organizationId || null}
            onChange={(v) => setForm((f) => ({ ...f, organizationId: v ?? '' }))}
            disabled={!canEdit}
            required
            searchable
          />

          <Group grow>
            <TextInput
              label="Фамилия"
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.currentTarget.value })}
              disabled={!canEdit}
              required
            />
            <TextInput
              label="Имя"
              value={form.firstName}
              onChange={(e) => setForm({ ...form, firstName: e.currentTarget.value })}
              disabled={!canEdit}
              required
            />
            <TextInput
              label="Отчество"
              value={form.middleName ?? ''}
              onChange={(e) => setForm({ ...form, middleName: e.currentTarget.value })}
              disabled={!canEdit}
            />
          </Group>

          <Group grow>
            <TextInput
              label="Должность"
              placeholder="Водитель, экспедитор…"
              value={form.position ?? ''}
              onChange={(e) => setForm({ ...form, position: e.currentTarget.value })}
              disabled={!canEdit}
            />
            <TextInput
              label="Табельный номер"
              value={form.personnelNumber ?? ''}
              onChange={(e) => setForm({ ...form, personnelNumber: e.currentTarget.value })}
              disabled={!canEdit}
            />
          </Group>

          <Text size="sm" fw={600} mt="xs">
            Паспортные данные
          </Text>
          <Text size="xs" c="dimmed" mt={-6}>
            Печатаются в доверенности М-2; без них выписать доверенность нельзя.
          </Text>

          <Group grow>
            <TextInput
              label="Серия"
              placeholder="4510"
              maxLength={4}
              value={form.passportSeries ?? ''}
              onChange={(e) => setForm({ ...form, passportSeries: e.currentTarget.value })}
              disabled={!canEdit}
            />
            <TextInput
              label="Номер"
              placeholder="123456"
              maxLength={6}
              value={form.passportNumber ?? ''}
              onChange={(e) => setForm({ ...form, passportNumber: e.currentTarget.value })}
              disabled={!canEdit}
            />
            <TextInput
              label="Код подразделения"
              placeholder="770-001"
              value={form.passportCode ?? ''}
              onChange={(e) => setForm({ ...form, passportCode: e.currentTarget.value })}
              disabled={!canEdit}
            />
          </Group>

          <TextInput
            label="Кем выдан"
            value={form.passportIssuedBy ?? ''}
            onChange={(e) => setForm({ ...form, passportIssuedBy: e.currentTarget.value })}
            disabled={!canEdit}
          />

          <TextInput
            label="Дата выдачи"
            type="date"
            value={form.passportIssueDate ?? ''}
            onChange={(e) => setForm({ ...form, passportIssueDate: e.currentTarget.value })}
            disabled={!canEdit}
          />

          <Text size="sm" fw={600} mt="xs">
            Дополнительно
          </Text>

          <Checkbox
            label="Водитель"
            checked={form.isDriver ?? false}
            onChange={(e) => setForm({ ...form, isDriver: e.currentTarget.checked })}
            disabled={!canEdit}
          />

          <Text size="xs" c="dimmed">
            Водительское удостоверение. В доверенности на водителя печатается вместо паспорта.
          </Text>

          <Group grow>
            <TextInput
              label="ВУ: серия"
              placeholder="5924"
              maxLength={4}
              value={form.driverLicenseSeries ?? ''}
              onChange={(e) => setForm({ ...form, driverLicenseSeries: e.currentTarget.value })}
              disabled={!canEdit}
            />
            <TextInput
              label="ВУ: номер"
              placeholder="525923"
              maxLength={6}
              value={form.driverLicenseNumber ?? ''}
              onChange={(e) => setForm({ ...form, driverLicenseNumber: e.currentTarget.value })}
              disabled={!canEdit}
            />
            <TextInput
              label="ВУ: дата выдачи"
              type="date"
              value={form.driverLicenseIssueDate ?? ''}
              onChange={(e) => setForm({ ...form, driverLicenseIssueDate: e.currentTarget.value })}
              disabled={!canEdit}
            />
          </Group>

          <TextInput
            label="ВУ: кем выдано"
            placeholder="ГИБДД 5938"
            value={form.driverLicenseIssuedBy ?? ''}
            onChange={(e) => setForm({ ...form, driverLicenseIssuedBy: e.currentTarget.value })}
            disabled={!canEdit}
          />

          <TextInput
            label="Телефон"
            value={form.phone ?? ''}
            onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })}
            disabled={!canEdit}
          />

          <TextInput
            label="ИНН"
            placeholder="12 цифр"
            maxLength={12}
            value={form.inn ?? ''}
            onChange={(e) => setForm({ ...form, inn: e.currentTarget.value })}
            disabled={!canEdit}
          />

          {canEdit && (
            <Button
              leftSection={<IconCheck size={16} />}
              onClick={handleSubmit}
              loading={saveMutation.isPending}
              mt="xs"
            >
              Сохранить
            </Button>
          )}
        </Stack>
      </Drawer>
    </RegistryPage>
  );
}
